import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { isEmpty } from "lodash";
import { Subscription } from "rxjs";
import { RouteType } from "src/app/models/enum";
import { UserFormRequestJSON, UserFormResponseJSON } from "src/app/models/models";
import {
	SuccessSnackbarComponent,
	SuccessSnackbarData,
} from "src/app/shared/components/cbta-snackbar/success-snackbar.component";
import { HasUnsavedFormChanges } from "src/app/shared/interfaces/has-unsaved-form-changes";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import {
	assignableUserRoles,
	formatUserRoleLabel,
	isDbOnlyUserRole,
	mergeAssignableUserRoles,
} from "src/app/shared/utils/user-role.utils";

@Component({
	selector: "app-edit-user",
	templateUrl: "./edit-user.component.html",
	styleUrls: ["./edit-user.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class EditUserComponent implements OnInit, HasUnsavedFormChanges {
	editUserForm!: UntypedFormGroup;
	userId: string = "";
	editMode: boolean = false;
	formSuccess: boolean = false;

	private routeParamSubscription!: Subscription;
	private snackBar = inject(MatSnackBar);
	/** Roles loaded from the database, including ones the form must not change. */
	private persistedRoles: string[] = [];
	sessionMasterData = this.sharedService.sessionMasterData;

	constructor(
		private fb: UntypedFormBuilder,
		private httpService: HttpService,
		private cd: ChangeDetectorRef,
		private loadingService: LoaderService,
		public sharedService: SharedService,
		private route: ActivatedRoute,
		private router: Router,
		private roleService: RoleService,
	) {
		this.initEditUserForm();
	}

	ngOnInit(): void {
		this.sharedService.setActivePage(RouteType.USER_MANAGEMENT);
	}

	initEditUserForm(): void {
		this.editUserForm = this.fb.group({
			name: ["", [Validators.required, Validators.maxLength(100)]],
			email: ["", [Validators.required, Validators.email]],
			role: [[], [Validators.required]],
		});

		this.routeParamSubscription = this.route.params.subscribe((params) => {
			this.userId = params["id"];
			if (!isEmpty(this.userId)) {
				this.fetchUserById();
			}
		});
	}

	fetchUserById(): void {
		this.loadingService.setLoading(true);
		this.httpService.getUser(this.userId).subscribe((resp: { data: UserFormResponseJSON }) => {
			this.loadForm(resp.data);
			this.loadingService.setLoading(false);
		});
	}

	loadForm(resp: UserFormResponseJSON): void {
		console.log("loadForm resp : ", JSON.stringify(resp));

		this.persistedRoles = Array.isArray(resp.roles) ? [...resp.roles] : [];
		const formData = {
			name: resp.name,
			email: resp.email,
			role: this.persistedRoles.filter((role) => !isDbOnlyUserRole(role)),
		};

		this.editUserForm.patchValue(formData);
		this.editUserForm.markAsPristine();
		this.cd.detectChanges();
		this.editMode = true;
	}

	get name() {
		return this.editUserForm.get("name")!;
	}

	get email() {
		return this.editUserForm.get("email")!;
	}

	get role() {
		return this.editUserForm.get("role")!;
	}

	get rolesOptions() {
		return assignableUserRoles(this.sessionMasterData?.roles);
	}

	removeRole(roleToRemove: string): void {
		const currentRoles = this.role.value || [];
		const updatedRoles = currentRoles.filter((role: string) => role !== roleToRemove);
		this.role.setValue(updatedRoles);
	}

	getRoleDisplayName(role: string): string {
		return formatUserRoleLabel(role);
	}

	onSubmit(): void {
		if (this.editUserForm.invalid) {
			return;
		}

		if (this.editUserForm.valid) {
			const formValue = this.editUserForm.value;

			const formData: UserFormRequestJSON = {
				name: String(formValue.name ?? "").trim().toUpperCase(),
				email: formValue.email,
				roles: mergeAssignableUserRoles(formValue.role, this.persistedRoles),
				aoc: this.roleService.getUserAOC(),
				is_external: formValue.external,
			};

			this.loadingService.setLoading(true);

			this.httpService.updateUser(this.userId, formData).subscribe({
				next: this.onSuccessFormSubmit.bind(this),
				error: this.onErrorFormSubmit.bind(this),
			});
		}
	}

	onSuccessFormSubmit(resp: any) {
		this.formSuccess = true;

		const userName = this.editUserForm.get("name")?.value;

		this.snackBar.openFromComponent(SuccessSnackbarComponent, {
			data: {
				userName: userName,
				action: "update",
			} as SuccessSnackbarData,
			duration: 4000,
			horizontalPosition: "center",
			verticalPosition: "top",
			panelClass: ["success-snackbar"],
		});

		setTimeout(() => {
			this.router.navigate([RouteType.USER_MANAGEMENT]);
		}, 2000);
		this.loadingService.setLoading(false);
	}

	onErrorFormSubmit(error: any): void {
		this.formSuccess = false;
		this.loadingService.setLoading(false);
	}

	hasUnsavedFormChanges(): boolean {
		return !this.formSuccess && this.editUserForm?.dirty === true;
	}

	goBack(): void {
		void this.router.navigate([RouteType.USER_MANAGEMENT]);
	}
}
